// src/components/SPanel/Home/homeLayout.js
//
// Pagina principală a elevului e un ecran de widget-uri, ca la iPhone: o grilă
// de pătrate (2 pe rând pe telefon, mai multe pe ecran lat) în care fiecare
// widget ocupă 1×1, 2×1 (lat) sau 2×2. Elevul le mută, le scoate, le adaugă și
// le schimbă mărimea (butonul „Editează”). Aranjamentul se salvează pe
// dispozitiv, pentru fiecare cont.

export const SIZES = {
   "1x1": { w: 1, h: 1, label: "Mic" },
   "2x1": { w: 2, h: 1, label: "Lat" },
   "2x2": { w: 2, h: 2, label: "Mare" },
};

/** Widget-urile disponibile, în ordinea din galerie. */
export const WIDGETS = [
   {
      id: "welcome",
      title: "Bun venit",
      description: "Salutul și pe scurt unde ai ajuns.",
      sizes: ["2x1"],
   },
   {
      id: "journey",
      title: "Drumul tău",
      description: "Lecțiile, una după alta, cu cea următoare în centru.",
      sizes: ["2x1"],
   },
   {
      id: "lessons",
      title: "Lecții",
      description: "Câte lecții ai făcut, câte sunt programate, orele la volan.",
      sizes: ["1x1", "2x1", "2x2"],
   },
   {
      id: "accuracy",
      title: "Întrebări",
      description: "Acuratețea tuturor răspunsurilor tale.",
      sizes: ["1x1", "2x1"],
   },
   {
      id: "practice",
      title: "Bilete și teme",
      description: "Câte bilete și teme ai trecut.",
      sizes: ["1x1", "2x1"],
   },
   {
      id: "activity",
      title: "Activitate",
      description: "În ce zile ai exersat și seria de zile la rând.",
      sizes: ["2x1"],
   },
   {
      id: "progress",
      title: "Progres pe zile",
      description: "Răspunsurile corecte și greșite pe zile, întrebările învățate.",
      sizes: ["2x1"],
   },
   {
      id: "next",
      title: "Lecția următoare",
      description: "Ziua, ora și cât mai e până la ea.",
      sizes: ["1x1"],
   },
   {
      id: "mistakes",
      title: "De recapitulat",
      description: "Întrebările greșite și biletele cu cele mai multe greșeli.",
      sizes: ["1x1"],
   },
   {
      id: "notifications",
      title: "Notificări",
      description: "Notificările necitite.",
      sizes: ["1x1"],
   },
   {
      id: "weather",
      title: "Vremea la lecție",
      description: "Prognoza pentru ora lecției următoare și ce să ai în vedere la volan.",
      sizes: ["1x1"],
   },
   {
      id: "path",
      title: "Spre permis",
      description: "Biletele, examenul teoretic, lecțiile și examenul practic.",
      sizes: ["2x1"],
   },
   {
      id: "videos",
      title: "Videoclipuri",
      description: "Cel mai nou videoclip recomandat.",
      sizes: ["1x1", "2x2"],
   },
   {
      id: "balance",
      title: "Balanță",
      description: "Soldul contului și ultimele operații.",
      sizes: ["1x1"],
      // apare doar când balanța e pornită pe server (GET /balance/config → enabled)
      requires: "balanceEnabled",
   },
];

/**
 * Widget-urile care cer o condiție (de ex. balanța pornită) dispar din grilă și din galerie
 * cât timp condiția lipsește; aranjamentul salvat nu se schimbă din cauza asta.
 */
export function isWidgetAvailable(id, flags = {}) {
   const requires = widgetInfo(id)?.requires;
   return !requires || flags[requires] === true;
}

const WIDGETS_BY_ID = new Map(WIDGETS.map((widget) => [widget.id, widget]));

export function widgetInfo(id) {
   return WIDGETS_BY_ID.get(id) || null;
}

/**
 * Aranjamentul inițial = pagina de dinainte de widget-uri: bun venit și
 * lecția următoare, drumul (cu vremea la lecție alături), lecțiile, biletele,
 * întrebările, apoi videoclipurile și pașii spre permis. Pe ecran lat (3 coloane) și pe
 * telefon (2 coloane) aceleași widget-uri, așezate pe măsura ecranului.
 * Celelalte stau în galerie („Editează” → „Adaugă”).
 */
const DEFAULT_WIDE = [
   { id: "welcome", size: "2x1" },
   { id: "next", size: "1x1" },
   { id: "journey", size: "2x1" },
   { id: "weather", size: "1x1" },
   { id: "lessons", size: "1x1" },
   { id: "practice", size: "1x1" },
   { id: "accuracy", size: "1x1" },
   { id: "videos", size: "1x1" },
   { id: "path", size: "2x1" },
   { id: "balance", size: "1x1" },
];

const DEFAULT_PHONE = [
   { id: "welcome", size: "2x1" },
   { id: "next", size: "1x1" },
   { id: "weather", size: "1x1" },
   { id: "journey", size: "2x1" },
   { id: "lessons", size: "1x1" },
   { id: "accuracy", size: "1x1" },
   { id: "practice", size: "2x1" },
   { id: "videos", size: "1x1" },
   { id: "balance", size: "1x1" },
   { id: "path", size: "2x1" },
];

export function defaultLayout(cols) {
   return (cols >= 3 ? DEFAULT_WIDE : DEFAULT_PHONE).map((item) => ({ ...item }));
}

/** Doar widget-uri cunoscute, fiecare o dată, cu o mărime permisă. */
export function sanitizeLayout(raw) {
   if (!Array.isArray(raw)) return null;
   const seen = new Set();
   const out = [];
   for (const item of raw) {
      const info = widgetInfo(item?.id);
      if (!info || seen.has(info.id)) continue;
      seen.add(info.id);
      out.push({
         id: info.id,
         size: info.sizes.includes(item.size) ? item.size : info.sizes[0],
      });
   }
   return out;
}

// v2: au venit widget-uri noi; aranjamentele vechi (v1) nu le aveau
// v3: aranjamentul implicit e din nou pagina de dinainte de widget-uri
const storageKey = (userId) => `home.widgets.v3:${userId ?? "anon"}`;

/** Aranjamentul salvat, sau null (= cel implicit pentru ecranul curent). */
export function loadLayout(userId) {
   try {
      const raw = localStorage.getItem(storageKey(userId));
      if (raw) return sanitizeLayout(JSON.parse(raw));
   } catch {
      // stocare blocată sau conținut stricat: aranjamentul implicit
   }
   return null;
}

/** Salvează aranjamentul; null îl șterge (înapoi la cel implicit). */
export function saveLayout(userId, layout) {
   try {
      if (layout) localStorage.setItem(storageKey(userId), JSON.stringify(layout));
      else localStorage.removeItem(storageKey(userId));
   } catch {
      // stocare blocată: aranjamentul ține până la reîncărcare
   }
}

/** Mută widget-ul `id` pe poziția `toIndex` (indexul din lista curentă). */
export function moveWidget(layout, id, toIndex) {
   const from = layout.findIndex((item) => item.id === id);
   if (from < 0 || toIndex < 0 || toIndex >= layout.length || from === toIndex) {
      return layout;
   }
   const next = layout.slice();
   const [item] = next.splice(from, 1);
   next.splice(toIndex, 0, item);
   return next;
}

export function removeWidget(layout, id) {
   return layout.filter((item) => item.id !== id);
}

export function addWidget(layout, id, size) {
   const info = widgetInfo(id);
   if (!info || layout.some((item) => item.id === id)) return layout;
   return [
      ...layout,
      { id, size: info.sizes.includes(size) ? size : info.sizes[0] },
   ];
}

/** Următoarea mărime permisă (circular): 1×1 → 2×1 → 2×2 → 1×1. */
export function cycleSize(layout, id) {
   return layout.map((item) => {
      if (item.id !== id) return item;
      const sizes = widgetInfo(id)?.sizes || [item.size];
      const index = sizes.indexOf(item.size);
      return { ...item, size: sizes[(index + 1) % sizes.length] };
   });
}

/** Widget-urile care nu sunt pe ecran (pentru „Adaugă”); cele indisponibile nu apar. */
export function availableWidgets(layout, flags = {}) {
   const used = new Set(layout.map((item) => item.id));
   return WIDGETS.filter((widget) => !used.has(widget.id) && isWidgetAvailable(widget.id, flags));
}

/**
 * Când balanța se pornește, elevul care are deja aranjamentul lui primește widget-ul o singură
 * dată, pe locul 2. Dacă îl scoate apoi, nu mai revine (ține minte `alreadyOffered`).
 */
export function offerBalanceWidget(layout, { enabled, alreadyOffered }) {
   if (!enabled || alreadyOffered || !Array.isArray(layout)) return null;
   if (layout.some((item) => item.id === "balance")) return null;
   const next = layout.slice();
   next.splice(Math.min(1, next.length), 0, { id: "balance", size: "1x1" });
   return next;
}

/** 2 coloane pe telefon și tabletă, 3 pe ecran lat (ca pagina de dinainte), 4 pe foarte lat. */
export function gridColumns(width) {
   if (width < 900) return 2;
   if (width < 1800) return 3;
   return 4;
}

/**
 * Înălțimea unui rând: pe telefon celulele sunt pătrate; pe ecran lat rândul
 * e mai jos decât lățimea (~cât blocurile paginii de dinainte).
 */
export function rowHeight(cell, cols) {
   return cols >= 3 ? Math.min(cell, 320) : cell;
}
